import { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Send, Loader2, Languages, X } from 'lucide-react';
import { toast } from 'sonner';

const LONG_PRESS_MS = 450;

export const BookingChat = ({ bookingId, otherPartyName }) => {
  const { user, api, token } = useAuth();
  const { language } = useLanguage();
  
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  // msgId -> { loading, text } map for inline translations triggered by long-press.
  const [translations, setTranslations] = useState({});
  const scrollRef = useRef(null);
  const pressTimerRef = useRef(null);

  const fetchMessages = useCallback(async () => {
    try {
      const response = await api().get(`/chat/${bookingId}`);
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  }, [bookingId, api]);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      await fetchMessages();
    };
    tick();
    const interval = setInterval(tick, 5000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [fetchMessages]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || sending) return;
    
    const messageText = input.trim();
    setInput('');
    setSending(true);

    try {
      const response = await api().post('/chat/message', {
        booking_id: bookingId,
        message: messageText
      });
      setMessages(prev => [...prev, response.data]);
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Long-press to translate a message into the user's current UI language.
  const translateMessage = useCallback(async (msg) => {
    if (translations[msg.id]?.text || translations[msg.id]?.loading) return;
    setTranslations((prev) => ({ ...prev, [msg.id]: { loading: true } }));
    try {
      const { data } = await api().post('/chuy/translate', {
        text: msg.message,
        target_lang: language,
      });
      setTranslations((prev) => ({ ...prev, [msg.id]: { loading: false, text: data.translated || '' } }));
    } catch (e) {
      setTranslations((prev) => {
        const next = { ...prev };
        delete next[msg.id];
        return next;
      });
      toast.error(e.response?.data?.detail || (language === 'en' ? 'Translation failed' : 'No se pudo traducir'));
    }
  }, [api, language, translations]);

  const startPress = (msg) => {
    if (pressTimerRef.current) clearTimeout(pressTimerRef.current);
    pressTimerRef.current = setTimeout(() => {
      pressTimerRef.current = null;
      // Haptic feedback on supported devices
      if (navigator.vibrate) { try { navigator.vibrate(20); } catch (_) { /* no-op */ } }
      translateMessage(msg);
    }, LONG_PRESS_MS);
  };
  const cancelPress = () => {
    if (pressTimerRef.current) {
      clearTimeout(pressTimerRef.current);
      pressTimerRef.current = null;
    }
  };
  const clearTranslation = (msgId) => {
    setTranslations((prev) => {
      const next = { ...prev };
      delete next[msgId];
      return next;
    });
  };

  const getPhotoUrl = (path) => {
    if (!path) return null;
    return `${process.env.REACT_APP_BACKEND_URL}/api/files/${path}?auth=${token}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="animate-spin text-caribbean" size={24} />
      </div>
    );
  }

  return (
    <div className="bg-card rounded-2xl shadow-lg overflow-hidden" data-testid="booking-chat">
      {/* Header */}
      <div className="bg-caribbean/10 px-4 py-3 border-b">
        <p className="font-semibold text-sm">
          {language === 'en' ? 'Chat with' : 'Chat con'} {otherPartyName}
        </p>
        <p className="text-[10px] text-muted-foreground mt-0.5 flex items-center gap-1">
          <Languages size={10} />
          {language === 'en'
            ? 'Hold any message to translate it'
            : 'Mantén presionado un mensaje para traducirlo'}
        </p>
      </div>

      {/* Messages */}
      <ScrollArea ref={scrollRef} className="h-[300px] p-4">
        <div className="space-y-3">
          {messages.length === 0 ? (
            <p className="text-center text-muted-foreground text-sm py-8">
              {language === 'en' 
                ? 'No messages yet. Start the conversation!'
                : '¡Sin mensajes aún. Inicia la conversación!'}
            </p>
          ) : (
            messages.map((msg) => {
              const isOwn = msg.sender_id === user?.id;
              return (
                <div
                  key={msg.id}
                  className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`flex items-end gap-2 max-w-[80%] ${isOwn ? 'flex-row-reverse' : ''}`}>
                    <div className="w-8 h-8 rounded-full bg-caribbean/10 flex items-center justify-center overflow-hidden flex-shrink-0">
                      {msg.sender_photo ? (
                        <img
                          src={getPhotoUrl(msg.sender_photo)}
                          alt={msg.sender_name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="font-heading text-caribbean text-xs font-bold">
                          {msg.sender_name?.charAt(0).toUpperCase()}
                        </span>
                      )}
                    </div>
                    <div
                      onTouchStart={() => startPress(msg)}
                      onTouchEnd={cancelPress}
                      onTouchMove={cancelPress}
                      onTouchCancel={cancelPress}
                      onMouseDown={() => startPress(msg)}
                      onMouseUp={cancelPress}
                      onMouseLeave={cancelPress}
                      onContextMenu={(e) => { e.preventDefault(); translateMessage(msg); }}
                      data-testid={`chat-msg-${msg.id}`}
                      className={`p-3 rounded-2xl select-none cursor-pointer ${
                        isOwn
                          ? 'bg-caribbean text-white rounded-br-sm'
                          : 'bg-muted rounded-bl-sm'
                      }`}
                      title={language === 'en' ? 'Hold to translate' : 'Mantén presionado para traducir'}
                    >
                      <p className="text-sm whitespace-pre-wrap">{msg.message}</p>

                      {/* Inline translation (long-press result) */}
                      {translations[msg.id] && (
                        <div
                          data-testid={`chat-msg-translation-${msg.id}`}
                          className={`mt-2 pt-2 border-t flex items-start gap-2 ${
                            isOwn ? 'border-white/30' : 'border-foreground/15'
                          }`}
                        >
                          <Languages size={12} className="shrink-0 mt-0.5 opacity-70" />
                          <div className="flex-1 min-w-0">
                            {translations[msg.id].loading ? (
                              <p className={`text-xs italic ${isOwn ? 'text-white/80' : 'text-muted-foreground'}`}>
                                {language === 'en' ? 'Translating…' : 'Traduciendo…'}
                              </p>
                            ) : (
                              <>
                                <p className={`text-[10px] uppercase font-bold tracking-wide ${isOwn ? 'text-white/80' : 'text-caribbean'}`}>
                                  {language === 'en' ? 'Translation' : 'Traducción'}
                                </p>
                                <p className={`text-sm whitespace-pre-wrap ${isOwn ? 'text-white' : 'text-foreground'}`}>
                                  {translations[msg.id].text}
                                </p>
                              </>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); clearTranslation(msg.id); }}
                            className={`shrink-0 w-5 h-5 rounded-full flex items-center justify-center ${
                              isOwn ? 'hover:bg-white/20 text-white/80' : 'hover:bg-foreground/10 text-muted-foreground'
                            }`}
                            aria-label="Close translation"
                          >
                            <X size={11} />
                          </button>
                        </div>
                      )}

                      <p className={`text-xs mt-1 ${isOwn ? 'text-white/60' : 'text-muted-foreground'}`}>
                        {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </ScrollArea>

      {/* Input */}
      <div className="p-3 border-t">
        <div className="flex items-center gap-2">
          <Input
            data-testid="chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={language === 'en' ? 'Type a message...' : 'Escribe un mensaje...'}
            className="flex-1 h-10 rounded-full"
            disabled={sending}
          />
          <Button
            data-testid="chat-send-btn"
            onClick={handleSend}
            disabled={sending || !input.trim()}
            size="icon"
            className="w-10 h-10 rounded-full bg-caribbean hover:bg-caribbean/90"
          >
            {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
          </Button>
        </div>
      </div>
    </div>
  );
};
